import { useMemo } from 'react';
import useSWR from 'swr';
import Cookies from 'js-cookie';
import { fetcherWithToken } from '@/shared/api/fetcher';
import type { GameCatalogItem } from './types';

const EMPTY_GAMES: GameCatalogItem[] = [];
const EMPTY_IDS: number[] = [];

interface GameApiOptions {
  enabled?: boolean;
}

export const useGamesCatalog = ({
  enabled = true,
}: GameApiOptions = {}) => {
  const { data: gameData, isLoading: isGamesLoading } = useSWR<
    GameCatalogItem[]
  >(enabled ? '/games' : null, fetcherWithToken);

  return {
    gameData: Array.isArray(gameData) ? gameData : EMPTY_GAMES,
    isGamesLoading: enabled ? isGamesLoading : false,
  };
};

const useRecommendedGames = () => {
  const token = Cookies.get('sessionId');
  const { data: recommendedData, isLoading: isRecommendedLoading } = useSWR(
    token ? '/recommendedGames' : null,
    fetcherWithToken,
    {
      dedupingInterval: 5000, // Не повторять запросы чаще 5 сек
      errorRetryCount: 1, // Максимум 1 повтор при ошибке
      shouldRetryOnError: false, // Отключить повтор при 500 ошибках
    }
  );

  const safeRecommendedData = useMemo(
    () => ({
      recommended: Array.isArray(recommendedData?.recommended)
        ? recommendedData.recommended
        : EMPTY_IDS,
      preferred: Array.isArray(recommendedData?.preferred)
        ? recommendedData.preferred
        : EMPTY_IDS,
    }),
    [recommendedData]
  );

  return {
    recommendedData: safeRecommendedData,
    isRecommendedLoading: token ? isRecommendedLoading : false,
  };
};

export const useGameApi = () => {
  const { gameData, isGamesLoading } = useGamesCatalog();
  const { recommendedData, isRecommendedLoading } = useRecommendedGames();

  return {
    gameData,
    isGamesLoading,
    recommendedData,
    isRecommendedLoading,
  };
};

export { useGameByRoute } from './use-game-by-route';
export { useGameLaunch } from './use-game-launch';
export { normalizeGameIds, useGamesByIds } from './use-games-by-ids';
export { getGamesByIds } from './get-games-by-ids';
